import {Component, Input, OnInit} from '@angular/core';
import {User} from "../../../../model/User";
import {OrganizationService} from "../../../service-data/organization.service";
import {UserService} from "../../../service-data/user.service";
import {ServerErrorToastService} from "../../../service-ui/server-error-toast.service";
import {LanguagePipe} from "../../../pipe/language.pipe";

@Component({
  selector: 'app-user',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.css']
})
export class UserComponent implements OnInit {
  @Input() user: User;

  /**
   * Cognito's view of whether this user still owes us a first sign-in.
   *
   * Three-valued on purpose:
   *   'UNKNOWN'      not asked yet, or the lookup failed
   *   'PENDING'      FORCE_CHANGE_PASSWORD -- a resend is meaningful
   *   'NOT_PENDING'  any other state, including no Cognito account at all
   *
   * The resend button is shown ONLY on 'PENDING'. 'UNKNOWN' hides it rather
   * than offering it hopefully: the server refuses anything that is not
   * FORCE_CHANGE_PASSWORD, so a button drawn on a guess can only fail.
   */
  invitePendingState = 'UNKNOWN';

  /** The raw Cognito enum, kept so a hidden button is explicable. */
  userStatus: string | null = null;

  constructor(public organizationService: OrganizationService, public userService: UserService,
              private serverErrorToastService: ServerErrorToastService) { }

  ngOnInit() {
    this.loadInviteStatus();
  }

  /**
   * One request per rendered user. Affordable here because this component draws
   * a single card; if it is ever reused inside a long list, the server has
   * offered a batch form built on Cognito's `ListUsers` -- ask for that rather
   * than firing one of these per row.
   *
   * Failure is silent by design. The status only decides whether to offer a
   * button, and an operator looking at a user card has not asked for anything
   * that warrants an error toast.
   */
  private loadInviteStatus () {
    if ( !this.user || !this.user.ID ) {
      return;
    }
    this.userService.getInviteStatus(this.user).subscribe(
      response => {
        if ( response && response.data ) {
          this.userStatus = response.data.userStatus;
          this.invitePendingState = response.data.invitePending ? 'PENDING' : 'NOT_PENDING';
        } else {
          this.invitePendingState = 'UNKNOWN';
        }
      },
      error => {
        console.error('getInviteStatus failed', error);
        this.invitePendingState = 'UNKNOWN';
      });
  }

  /** True only when Cognito says the invitation has not yet been accepted. */
  canResendEmailInvite (): boolean {
    return this.invitePendingState === 'PENDING';
  }

  // ---------------------------------------------------------------------------
  // Every call below changes what somebody can GET INTO, so none may fail
  // silently.
  //
  // These used to be `subscribe(x => { ...mark it done... })` with no error
  // handler, over services whose `catchError` returned `of(result)`. An
  // observable that emits is indistinguishable from success, so a failed
  // revoke still ran the success branch: the button flipped to "Grant
  // Administrative Privileges" and the operator walked away believing they had
  // removed someone's admin rights. Nobody re-checks an operation they were
  // told had worked, which is what makes this worse than a visible failure.
  //
  // The services now rethrow (`failLoudly`), and each subscription carries an
  // error handler that shows the server's own sentence and leaves the UI
  // displaying the UNCHANGED state.
  // ---------------------------------------------------------------------------

  revokeOrganizationAdmin (user: User) {
    this.organizationService.revokeOrganizationAdmin(user).subscribe(
      x => {
        user.isAdmin = 'FALSE';
        this.serverErrorToastService.reportSuccess(LanguagePipe.get('Administrative privileges revoked', 'compressSpaces'));
      },
      error => {
        // Deliberately does NOT touch user.isAdmin - the flag still reflects
        // the server's state, which is that nothing changed.
        this.serverErrorToastService.reportError('revokeOrganizationAdmin', error,
          LanguagePipe.get('Could not revoke administrative privileges. The user still has them.', 'compressSpaces'));
      });
  }

  grantOrganizationAdmin (user: User) {
    this.organizationService.grantOrganizationAdmin(user).subscribe(
      x => {
        user.isAdmin = 'TRUE';
        this.serverErrorToastService.reportSuccess(LanguagePipe.get('Administrative privileges granted', 'compressSpaces'));
      },
      error => {
        this.serverErrorToastService.reportError('grantOrganizationAdmin', error,
          LanguagePipe.get('Could not grant administrative privileges. The user does not have them.', 'compressSpaces'));
      });
  }

  /**
   * Re-send the Cognito invitation email.
   *
   * Until 2026-09-02 this button DELETED the user's Cognito account and made a
   * new one -- see the note on `UserService.resendEmailInvite`. It is now
   * non-destructive, and the server refuses it for anyone who has finished
   * signing up, so the worst outcome here is a refusal.
   *
   * Re-checked despite the template guard: `invitePendingState` is a snapshot
   * taken at init, and the user may have accepted the invitation since.
   */
  resendEmailInvite (user: User) {
    if ( !this.canResendEmailInvite() ) {
      return;
    }
    this.userService.resendEmailInvite(user).subscribe(
      x => {
        this.serverErrorToastService.reportSuccess(LanguagePipe.get('Invitation sent', 'compressSpaces'));
      },
      error => {
        this.serverErrorToastService.reportError('resendEmailInvite', error, this.getResendErrorMessage(error));
        // A refusal is also fresh information about the account, so take it.
        // NOT_INVITE_PENDING means our snapshot was stale and the button should
        // not have been offered -- withdraw it rather than leave it inviting a
        // second identical failure.
        const data = this.getErrorData(error);
        if ( data && data.code === 'NOT_INVITE_PENDING' ) {
          this.invitePendingState = 'NOT_PENDING';
          if ( data.userStatus ) {
            this.userStatus = data.userStatus;
          }
        }
        if ( data && data.code === 'NO_COGNITO_USER' ) {
          this.invitePendingState = 'NOT_PENDING';
          this.userStatus = null;
        }
      });
  }

  /**
   * The server sends `ResponseMessage` -- `{ message, status, data }` -- so the
   * machine-readable reason sits at `error.error.data`, one level deeper than
   * the HttpErrorResponse. Dug out defensively: a network failure or a gateway
   * error has no body at all.
   */
  private getErrorData (error: any): any {
    if ( error && error.error && error.error.data ) {
      return error.error.data;
    }
    return undefined;
  }

  /**
   * Branch on `data.code`, not on the HTTP status: `NOT_FOUND` and
   * `NO_COGNITO_USER` are both 404 and mean quite different things to whoever
   * has to fix them.
   */
  private getResendErrorMessage (error: any): string {
    const data = this.getErrorData(error);
    const code = data ? data.code : '';
    if ( code === 'NOT_INVITE_PENDING' ) {
      return LanguagePipe.get('This user has already finished signing up, so there is no invitation to resend.', 'compressSpaces');
    }
    if ( code === 'NO_COGNITO_USER' ) {
      return LanguagePipe.get('This user has no sign-in account yet, so there is no invitation to resend.', 'compressSpaces');
    }
    if ( code === 'NOT_FOUND' ) {
      return LanguagePipe.get('That user could not be found.', 'compressSpaces');
    }
    return LanguagePipe.get('Could not resend the invitation.', 'compressSpaces');
  }

  disable (user: User) {
    this.userService.disable(user).subscribe(
      usr => {
        this.serverErrorToastService.reportSuccess(LanguagePipe.get('User disabled', 'compressSpaces'));
      },
      error => {
        this.serverErrorToastService.reportError('disableUser', error,
          LanguagePipe.get('Could not disable the user. They still have access.', 'compressSpaces'));
      });
  }

  enable (user: User) {
    this.userService.enable(user).subscribe(
      usr => {
        this.serverErrorToastService.reportSuccess(LanguagePipe.get('User enabled', 'compressSpaces'));
      },
      error => {
        this.serverErrorToastService.reportError('enableUser', error,
          LanguagePipe.get('Could not enable the user.', 'compressSpaces'));
      });
  }
}
